// Belege im Arbeitsplatz (Plan 2026-10-18 P4, Nutzerentscheidung 18.10.): keine starre
// Zuordnung Beleg -> Schritt. Jeder Container (und die Bestellung) nimmt jederzeit beliebig
// viele Belege an, jeder Beleg ist jederzeit herunterladbar. Der Typ ist nur Etikett und
// Filter — kein Schritt haengt an einem Beleg, der Schrittstatus kommt weiter vom Server.
import { useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { PurchaseDocument } from "@/api/purchase";
import { dokumentHerunterladen, useAblegenDocument, useAssignDocument } from "@/api/purchase";
import { Button } from "@/components/ui/button";
import { fehlerText } from "@/lib/fehlerText";
import { docStatusLabel, docTypLabel } from "@/lib/purchase-format";

const OHNE_TYP = "ohne Typ";
const typEtikett = (d: PurchaseDocument) => (d.typ ? docTypLabel(d.typ) : OHNE_TYP);

/**
 * Knopf + verstecktes Dateifeld; mehrere Dateien moeglich, Ziel steht im Feldnamen.
 * `knopfText`/`feldLabel`/`hauptknopf`: dieselbe Mechanik als Hauptknopf „Packliste hochladen“.
 */
export function BelegHochladen({ zielText, laeuft, onDateien, className, knopfText = "Beleg hochladen", feldLabel, hauptknopf = false }: {
  zielText: string;
  laeuft: boolean;
  onDateien: (dateien: File[]) => void;
  className?: string;
  knopfText?: string;
  feldLabel?: string;
  hauptknopf?: boolean;
}) {
  const feld = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={feld}
        type="file"
        multiple
        className="hidden"
        aria-label={feldLabel ?? `Beleg für ${zielText} auswählen`}
        onChange={(e) => {
          const dateien = Array.from(e.target.files ?? []);
          e.target.value = "";
          if (dateien.length > 0) onDateien(dateien);
        }}
      />
      <Button type="button" variant={hauptknopf ? "default" : "outline"} size={hauptknopf ? "default" : "sm"}
        className={hauptknopf ? `font-semibold ${className ?? ""}` : className} disabled={laeuft} onClick={() => feld.current?.click()}>
        {laeuft ? <Loader2 className="mr-1 size-3.5 animate-spin" /> : null}
        {knopfText}
      </Button>
    </>
  );
}

/**
 * Pruefen, Ablegen, Ablehnen, Zuordnen, Korrigieren, Loeschen je Beleg (Schnitt 3, 22.10.2026).
 * Pruefen, Ablehnen, Korrigieren und Loeschen oeffnen die Dialoge der Akte (Grund Pflicht).
 */
export interface BelegAktionen {
  orderId: number;
  /** Moegliche neue Orte: Bestellung (`null`) und nicht gesperrte Container, ohne den aktuellen. */
  ziele: Array<{ shipmentId: number | null; titel: string }>;
  pruefen: (documentId: number) => void;
  ablehnen: (documentId: number) => void;
  korrigieren: (documentId: number) => void;
  loeschen: (documentId: number) => void;
}

/** Wie im Server (`assignDocument`, `ablegeDocument`): nur neue oder ausgelesene Belege. */
const ZUORDENBAR = new Set(["neu", "extrahiert"]);
/** Bestaetigte Packliste/Kombibeleg: der Server loescht sie nicht, sie wird ueber „Korrigieren“ wieder geoeffnet. */
const istKorrigierbar = (d: PurchaseDocument) =>
  d.agent_status === "bestaetigt" && (d.typ === "packing_list" || d.typ === "invoice_packing_combo");

function BelegAktionsZeile({ beleg, name, aktionen }: { beleg: PurchaseDocument; name: string; aktionen: BelegAktionen }) {
  const assignDoc = useAssignDocument();
  const ablegenDoc = useAblegenDocument();
  const knopf = "rounded-sm px-1 text-[11px] font-semibold text-ink-2 hover:text-ink focus-visible:outline-2 focus-visible:outline-primary";
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1.5 pl-7">
      {ZUORDENBAR.has(beleg.agent_status) && aktionen.ziele.length > 0 ? (
        <select
          aria-label={`${name} zuordnen`}
          value=""
          disabled={assignDoc.isPending}
          onChange={(e) => {
            const ziel = aktionen.ziele.find((z) => String(z.shipmentId ?? "bestellung") === e.target.value);
            if (!ziel) return;
            assignDoc.mutate(
              { docId: beleg.id, orderId: aktionen.orderId, shipmentId: ziel.shipmentId },
              {
                onSuccess: () => toast.success(`Zugeordnet: ${name} → ${ziel.titel}`),
                onError: (fehler) => toast.error(fehlerText(fehler, "Zuordnen fehlgeschlagen")),
              },
            );
          }}
          className="h-6 max-w-36 rounded border border-line bg-background px-1 text-[11px] text-ink"
        >
          <option value="" disabled>Zuordnen…</option>
          {aktionen.ziele.map((z) => (
            <option key={z.shipmentId ?? "bestellung"} value={String(z.shipmentId ?? "bestellung")}>{z.titel}</option>
          ))}
        </select>
      ) : null}
      {beleg.agent_status === "extrahiert" ? (
        <button type="button" aria-label={`${name} prüfen`} className={`${knopf} text-primary hover:text-primary`}
          onClick={() => aktionen.pruefen(beleg.id)}>
          Prüfen
        </button>
      ) : null}
      {ZUORDENBAR.has(beleg.agent_status) ? (
        <button
          type="button"
          aria-label={`${name} ablegen`}
          title="Ohne (weitere) Auswertung ablegen"
          className={knopf}
          disabled={ablegenDoc.isPending}
          onClick={() => {
            if (!window.confirm(`„${name}“ ohne Auswertung ablegen? Positionen und Termine bleiben unverändert.`)) return;
            ablegenDoc.mutate(
              { docId: beleg.id },
              {
                onSuccess: () => toast.success("Dokument abgelegt (ohne Auswertung)"),
                onError: (fehler) => toast.error(fehlerText(fehler, "Ablegen fehlgeschlagen")),
              },
            );
          }}
        >
          Ablegen
        </button>
      ) : null}
      {beleg.agent_status === "extrahiert" ? (
        <button type="button" aria-label={`${name} ablehnen`} className={knopf} onClick={() => aktionen.ablehnen(beleg.id)}>
          Ablehnen
        </button>
      ) : null}
      {istKorrigierbar(beleg) ? (
        <button type="button" aria-label={`${name} korrigieren`} className={knopf} onClick={() => aktionen.korrigieren(beleg.id)}>
          Korrigieren
        </button>
      ) : (
        <button type="button" aria-label={`${name} löschen`} className={`${knopf} text-coral hover:text-coral`}
          onClick={() => aktionen.loeschen(beleg.id)}>
          Löschen
        </button>
      )}
    </div>
  );
}

/**
 * Liste mit Typ-Filter und Download je Beleg (Panel rechts); mit `aktionen` auch pruefen, ablegen,
 * ablehnen, zuordnen, korrigieren, loeschen. Ohne `onDateien` (kein Recht) gibt es keinen Upload-Knopf.
 */
export function BelegeListe({ belege, zielText, laeuft, onDateien, aktionen }: {
  belege: PurchaseDocument[];
  zielText: string;
  laeuft: boolean;
  onDateien?: (dateien: File[]) => void;
  aktionen?: BelegAktionen;
}) {
  const [filter, setFilter] = useState<string | null>(null);
  const [laedt, setLaedt] = useState<number | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const etiketten = [...new Set(belege.map(typEtikett))];
  // Ein Filter, dessen Typ nicht mehr vorkommt (anderer Container), gilt nicht mehr.
  const aktiv = filter !== null && etiketten.includes(filter) ? filter : null;
  const sichtbar = aktiv === null ? belege : belege.filter((d) => typEtikett(d) === aktiv);

  const herunterladen = async (d: PurchaseDocument) => {
    setFehler(null);
    setLaedt(d.id);
    try {
      await dokumentHerunterladen(d.id, d.file_name || `document-${d.id}.pdf`);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Download fehlgeschlagen");
    } finally {
      setLaedt(null);
    }
  };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-2">
      {etiketten.length > 1 ? (
        <div className="flex flex-wrap gap-1" role="group" aria-label="Belege nach Typ filtern">
          {[null, ...etiketten].map((e) => (
            <button
              key={e ?? "alle"}
              type="button"
              aria-label={`Filter ${e ?? "Alle"}`}
              aria-pressed={aktiv === e}
              onClick={() => setFilter(e)}
              className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold focus-visible:outline-2 focus-visible:outline-primary ${
                aktiv === e ? "border-primary bg-primary text-primary-foreground" : "border-line bg-background text-ink-2 hover:text-ink"
              }`}
            >
              {e ?? "Alle"}
            </button>
          ))}
        </div>
      ) : null}
      {belege.length === 0 ? (
        <p className="text-xs text-ink-2">{onDateien ? "Noch keine Belege. Jeder Beleg kann jederzeit hochgeladen werden." : "Noch keine Belege."}</p>
      ) : (
        <ul className="grid grid-cols-[minmax(0,1fr)] gap-1 text-xs">
          {sichtbar.map((d) => {
            const name = d.file_name || `Dokument #${d.id}`;
            return (
              <li key={d.id} className="grid grid-cols-[minmax(0,1fr)] gap-0.5">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    aria-label={`${name} herunterladen`}
                    title="Herunterladen"
                    disabled={laedt === d.id}
                    onClick={() => void herunterladen(d)}
                    className="grid size-5 shrink-0 place-items-center rounded-sm bg-panel-2 text-[9px] font-bold text-ink-2 hover:bg-primary hover:text-primary-foreground focus-visible:outline-2 focus-visible:outline-primary"
                  >
                    {laedt === d.id ? <Loader2 className="size-3 animate-spin" /> : "↓"}
                  </button>
                  <span className="min-w-0 flex-1">
                    <span title={name} className="line-clamp-2 text-ink [overflow-wrap:anywhere]">{name}</span>
                    <span className="block text-[11px] text-ink-2">{docStatusLabel(d.agent_status)}</span>
                  </span>
                  <span className="shrink-0 rounded-full border border-line px-1.5 py-px text-[10px] text-ink-2">{typEtikett(d)}</span>
                </div>
                {aktionen ? <BelegAktionsZeile beleg={d} name={name} aktionen={aktionen} /> : null}
              </li>
            );
          })}
        </ul>
      )}
      {fehler ? <p role="alert" className="text-xs text-coral">Download fehlgeschlagen: {fehler}</p> : null}
      {onDateien ? <BelegHochladen zielText={zielText} laeuft={laeuft} onDateien={onDateien} className="w-full" /> : null}
    </div>
  );
}
